import {
  AloftDate,
  AnalysisPeriod,
  expected,
  isNull,
  isUndefined,
  syntaxFNMA,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { TrendsChartDataType } from '../../../../modules/marketConditions/utils/getHousingTrendsChartData.ts';
import { MonthGrouping } from '../../../../modules/marketConditions/zillow/types.ts';
import {
  DateOfSaleMethodInput,
  selectCustomMonthlyPriceMethodInput,
  selectMonthlyPriceInputFromCustomRate,
  selectZillowMethodInput,
} from './selectDateOfSaleOrListToSaleAdjustment.ts';

type MaxRowSize = 12 | 24;

export type MarketAdjustmentsChartDisplay = {
  data: TrendsChartDataType[];
  effectiveDate: AloftDate;
  updatedAt?: AloftDate;
  isZillow: boolean;
};

export type MarketAdjustmentsTableDisplay = {
  value: string;
  isProjection?: boolean;
};

function filterAnalysisPeriod({
  effectiveDate,
  analysisPeriod,
  historical,
}: {
  effectiveDate: AloftDate;
  analysisPeriod: AnalysisPeriod;
  historical: MonthGrouping[];
}): MonthGrouping[] | null {
  const startOfPeriod = effectiveDate.subtractMonths(analysisPeriod);
  const startOfPeriodAsISOMonth = syntaxFNMA.aloftDateToIsoMonth(startOfPeriod);
  const startIndex = historical.findIndex((val) => val.month === startOfPeriodAsISOMonth);

  if (isUndefined(startIndex) || startIndex === -1) return null;

  return historical.slice(startIndex + 1);
}

// =============================================
// ============== CHART SELECTORS ==============
// =============================================

function getZillowOrCustomMonthlyPriceChartDisplayInnerFn({
  input,
  analysisPeriod,
  updatedAt,
  method,
}: {
  input: {
    effectiveDate: AloftDate;
    historical: MonthGrouping[];
    projection?: MonthGrouping[];
  };
  analysisPeriod: AnalysisPeriod | null;
  updatedAt?: AloftDate;
  method: 'zillow' | 'customMonthlyPrice';
}): MarketAdjustmentsChartDisplay | null {
  const { effectiveDate, historical, projection = [] } = input;

  const maybeFilteredHistorical = isNull(analysisPeriod)
    ? historical
    : filterAnalysisPeriod({ effectiveDate, historical, analysisPeriod });

  if (isNull(maybeFilteredHistorical)) return null;

  const historicalAndProjectionData = [...maybeFilteredHistorical, ...projection];

  const chartData: TrendsChartDataType[] = [];

  for (let i = 0; i < historicalAndProjectionData.length; i += 1) {
    const { month, typicalValue, affectedComps } = expected(historicalAndProjectionData[i]);

    const isProjection = projection.some((p) => p.month === month);
    const isNextMonthProjection = projection.some(
      (p) => p.month === historicalAndProjectionData[i + 1]?.month,
    );

    chartData.push({
      inner: expected(historicalAndProjectionData[i]),
      month,
      historical: isProjection ? null : typicalValue.toDollarLike(),
      projection:
        isProjection || isNextMonthProjection // hack for projection line
          ? typicalValue.toDollarLike()
          : null,
      comps: affectedComps,
    });
  }

  return {
    data: chartData,
    effectiveDate,
    updatedAt,
    isZillow: method === 'zillow',
  };
}

export function getZillowMethodChartDisplay({
  input,
  analysisPeriod,
}: {
  input: DateOfSaleMethodInput.Zillow | undefined;
  analysisPeriod: AnalysisPeriod;
}): ReturnType<typeof getZillowOrCustomMonthlyPriceChartDisplayInnerFn> {
  if (isUndefined(input)) return null;

  return getZillowOrCustomMonthlyPriceChartDisplayInnerFn({
    analysisPeriod,
    input: {
      effectiveDate: input.effectiveDate,
      historical: input.historical,
      projection: input.projection,
    },
    updatedAt: input.updatedAt,
    method: 'zillow',
  });
}

export const selectZillowMethodChartDisplay = createSelector(
  [selectZillowMethodInput],
  (input) => (analysisPeriod: AnalysisPeriod) =>
    getZillowMethodChartDisplay({ input, analysisPeriod }),
);

export const getCustomMonthlyPriceMethodChartDisplay = (
  input?: DateOfSaleMethodInput.CustomMonthlyPrice,
): ReturnType<typeof getZillowOrCustomMonthlyPriceChartDisplayInnerFn> => {
  if (!input) return null;

  return getZillowOrCustomMonthlyPriceChartDisplayInnerFn({
    analysisPeriod: null,
    input: {
      effectiveDate: input.effectiveDate,
      historical: input.historical,
      projection: undefined,
    },
    method: 'customMonthlyPrice',
  });
};

export const selectCustomMonthlyRateMethodChartDisplay = createSelector(
  [selectMonthlyPriceInputFromCustomRate],
  (input) => getCustomMonthlyPriceMethodChartDisplay(input),
);

export const selectCustomMonthlyPriceMethodChartDisplay = createSelector(
  [selectCustomMonthlyPriceMethodInput],
  (input) => getCustomMonthlyPriceMethodChartDisplay(input),
);

// =============================================
// ============== TABLE SELECTORS ==============
// =============================================

function getZillowOrCustomMonthlyPriceTableDisplayInnerFn({
  input,
  analysisPeriod,
}: {
  input: {
    effectiveDate: AloftDate;
    historical: MonthGrouping[];
    projection?: MonthGrouping[];
  };
  analysisPeriod: AnalysisPeriod | null;
}):
  | [
      MarketAdjustmentsTableDisplay[],
      MarketAdjustmentsTableDisplay[],
      MarketAdjustmentsTableDisplay[],
      MarketAdjustmentsTableDisplay[],
    ]
  | null {
  const { effectiveDate, historical, projection = [] } = input;

  const maybeFilteredHistorical = isNull(analysisPeriod)
    ? historical
    : filterAnalysisPeriod({ effectiveDate, historical, analysisPeriod });

  if (isNull(maybeFilteredHistorical)) return null;

  const data = [...maybeFilteredHistorical, ...projection];

  const headerRow: MarketAdjustmentsTableDisplay[] = [{ value: '' }];
  const typicalValueRow: MarketAdjustmentsTableDisplay[] = [{ value: 'Typical Home Value' }];
  const adjustmentRow: MarketAdjustmentsTableDisplay[] = [{ value: 'Date of Sale Adj.' }];
  const affectedCompsRow: MarketAdjustmentsTableDisplay[] = [{ value: 'Comps Affected' }];

  for (let i = 0; i < data.length; i += 1) {
    const { month, typicalValue, percentChange, affectedComps } = expected(data[i]);
    const isProjection = projection.some((p) => p.month === month);

    headerRow.push({ value: month });
    typicalValueRow.push({ value: typicalValue.toString(true), isProjection });
    adjustmentRow.push({ value: `${percentChange.toFixed(2)}%`, isProjection });
    affectedCompsRow.push({ value: affectedComps ?? '' });
  }

  return [headerRow, typicalValueRow, adjustmentRow, affectedCompsRow];
}

// lets us split 24-month data into two tables
function splitRows({
  data,
  maxRowSize,
}: {
  data: ReturnType<typeof getZillowOrCustomMonthlyPriceTableDisplayInnerFn>;
  maxRowSize: MaxRowSize;
}): MarketAdjustmentsTableDisplay[][][] | null {
  if (isNull(data)) return null;

  if (maxRowSize === 24) {
    return [data];
  }

  if (maxRowSize === 12 && data[0].length <= 13) {
    return [data];
  }

  const firstTable: MarketAdjustmentsTableDisplay[][] = [];
  const secondTable: MarketAdjustmentsTableDisplay[][] = [];

  for (let i = 0; i < data.length; i += 1) {
    const row = expected(data[i]);
    const firstColumn = expected(row[0]);

    firstTable.push([firstColumn, ...row.slice(1, 13)]);
    secondTable.push([firstColumn, ...row.slice(13)]);
  }

  return [firstTable, secondTable];
}

export function getZillowMethodTableDisplay({
  input,
  analysisPeriod,
  maxRowSize,
}: {
  input?: DateOfSaleMethodInput.Zillow;
  analysisPeriod: AnalysisPeriod;
  maxRowSize?: MaxRowSize;
}): MarketAdjustmentsTableDisplay[][][] | null {
  if (!input) return null;

  const data = getZillowOrCustomMonthlyPriceTableDisplayInnerFn({
    analysisPeriod,
    input: {
      effectiveDate: input.effectiveDate,
      historical: input.historical,
      projection: input.projection,
    },
  });

  return splitRows({ data, maxRowSize: maxRowSize ?? 24 });
}

export const selectZillowMethodTableDisplay = createSelector(
  [selectZillowMethodInput],
  (input) => (analysisPeriod: AnalysisPeriod, maxRowSize?: MaxRowSize) =>
    getZillowMethodTableDisplay({ input, analysisPeriod, maxRowSize }),
);

export const getCustomMonthlyPriceMethodTableDisplay = ({
  input,
  maxRowSize,
}: {
  input?: DateOfSaleMethodInput.CustomMonthlyPrice;
  maxRowSize?: MaxRowSize;
}): MarketAdjustmentsTableDisplay[][][] | null => {
  if (!input) return null;

  const data = getZillowOrCustomMonthlyPriceTableDisplayInnerFn({
    analysisPeriod: null,
    input: {
      effectiveDate: input.effectiveDate,
      historical: input.historical,
      projection: undefined,
    },
  });

  return splitRows({ data, maxRowSize: maxRowSize ?? 24 });
};

export const selectCustomMonthlyRateMethodTableDisplay = createSelector(
  [selectMonthlyPriceInputFromCustomRate],
  (input) => getCustomMonthlyPriceMethodTableDisplay({ input, maxRowSize: 12 }),
);

export const selectCustomMonthlyPriceMethodTableDisplay = createSelector(
  [selectCustomMonthlyPriceMethodInput],
  (input) => getCustomMonthlyPriceMethodTableDisplay({ input, maxRowSize: 12 }),
);
